import React, { useState, useEffect } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import * as XLSX from "xlsx";
import { IoMdClose } from "react-icons/io";

// Helper function to determine if a value is likely numeric
const isNumeric = (val) => {
  if (val === null || val === undefined) return false;
  // Remove common currency, percentage, and thousands separators
  const cleanedVal = String(val).replace(/[\s,₹$€£%]/g, "");
  if (cleanedVal === "" || cleanedVal === "--" || cleanedVal === "-")
    return false;
  return !isNaN(parseFloat(cleanedVal)) && isFinite(cleanedVal);
};

const AuctionInsightsUploadChart = ({ arrayIndex, removeArrayIndex }) => {
  // State for managing chart and data
  const [originalData, setOriginalData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [stringColumns, setStringColumns] = useState([]);
  const [numericColumns, setNumericColumns] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // State for user selections
  const [labelColumn, setLabelColumn] = useState(""); // X-axis
  const [selectedMetric, setSelectedMetric] = useState(""); // Y-axis
  const [chartType, setChartType] = useState("pie");

  // Handles the file upload and processing
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/)) {
      setError("Please upload a valid Excel file (.xlsx or .xls).");
      return;
    }

    setLoading(true);
    setError(null);
    setOriginalData([]);
    setStringColumns([]);
    setNumericColumns([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (rows.length === 0) throw new Error("The uploaded file is empty.");

        let headerRowIndex = -1;
        for (let i = 0; i < Math.min(rows.length, 10); i++) {
          if (
            Array.isArray(rows[i]) &&
            rows[i].filter(
              (cell) => cell != null && cell.toString().trim() !== ""
            ).length >= 2
          ) {
            headerRowIndex = i;
            break;
          }
        }

        if (headerRowIndex === -1)
          throw new Error(
            "Could not find a valid header row. Please ensure the data table starts within the first 10 rows."
          );

        const headerArray = rows[headerRowIndex].filter((h) => h);
        const dataRows = rows.slice(headerRowIndex + 1);
        const firstHeader = headerArray[0];

        const data = dataRows
          .map((row) => {
            const rowData = {};
            headerArray.forEach((header, index) => {
              rowData[header] = row[index];
            });
            return rowData;
          })
          .filter((row) => {
            const firstColumnValue = row[firstHeader];
            const isTotalRow =
              firstColumnValue &&
              firstColumnValue
                .toString()
                .trim()
                .toLowerCase()
                .includes("total");
            const isNotEmpty = Object.values(row).some((val) => val != null);
            return isNotEmpty && !isTotalRow;
          });

        if (data.length === 0)
          throw new Error("No valid data rows found below the header.");

        const finalHeaders = Object.keys(data[0]);
        const stringCols = [];
        const numericCols = [];

        finalHeaders.forEach((header) => {
          let numericCount = 0;
          const sampleSize = Math.min(data.length, 10);
          for (let i = 0; i < sampleSize; i++) {
            if (isNumeric(data[i][header])) {
              numericCount++;
            }
          }
          if (numericCount / sampleSize > 0.7) {
            numericCols.push(header);
          } else {
            stringCols.push(header);
          }
        });

        if (stringCols.length === 0 || numericCols.length === 0) {
          throw new Error(
            "The file must contain at least one text column (for labels) and one numeric column (for values)."
          );
        }

        setStringColumns(stringCols);
        setNumericColumns(numericCols);
        setOriginalData(data);
        setLabelColumn(stringCols[0]);
        setSelectedMetric(numericCols[0]);
      } catch (err) {
        console.error("Error processing file:", err);
        setError(err.message || "Failed to process the uploaded file.");
        setOriginalData([]);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  // Effect to update the chart when data or selections change
  useEffect(() => {
    if (originalData.length > 0 && selectedMetric && labelColumn) {
      // *** FIX: Aggregate data by the selected label column ***
      const aggregationMap = new Map();

      originalData.forEach((item) => {
        const label = String(item[labelColumn] || "");
        const value =
          parseFloat(String(item[selectedMetric]).replace(/[^0-9.-]+/g, "")) ||
          0;

        // If the label doesn't exist in the map, add it. Otherwise, add to the existing value.
        aggregationMap.set(label, (aggregationMap.get(label) || 0) + value);
      });

      // Convert the aggregated map back to the array format the chart needs
      const aggregatedData = Array.from(
        aggregationMap,
        ([label, value], index) => ({
          id: index,
          label,
          value,
        })
      );

      setChartData(aggregatedData);
    } else {
      setChartData([]);
    }
  }, [originalData, selectedMetric, labelColumn]);

  const hasMeaningfulData =
    chartData.reduce((sum, item) => sum + item.value, 0) > 0;

  // Renders the appropriate chart based on the selected type
  const renderChart = () => {
    if (!hasMeaningfulData) {
      return (
        <div className="text-center text-gray-500 py-8 text-sm">
          No data available to display for the selected metric.
        </div>
      );
    }

    switch (chartType) {
      case "pie":
        return (
          <PieChart
            series={[
              {
                data: chartData,
                highlightScope: { faded: "global", highlighted: "item" },
                faded: {
                  innerRadius: 30,
                  additionalRadius: -30,
                  color: "gray",
                },
                valueFormatter: (item) => `${item.value.toLocaleString()}`,
              },
            ]}
            slotProps={{ legend: { labelStyle: { fontSize: 12 } } }}
            height={300}
          />
        );
      case "line":
        return (
          <LineChart
            xAxis={[
              { scaleType: "point", data: chartData.map((item) => item.label) },
            ]}
            series={[
              {
                data: chartData.map((item) => item.value),
                valueFormatter: (value) =>
                  value ? value.toLocaleString() : "0",
              },
            ]}
            height={300}
          />
        );
      case "bar":
        return (
          <BarChart
            xAxis={[
              { scaleType: "band", data: chartData.map((item) => item.label) },
            ]}
            series={[
              {
                data: chartData.map((item) => item.value),
                valueFormatter: (value) =>
                  value ? value.toLocaleString() : "0",
              },
            ]}
            height={300}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="mt-8 border border-gray-200 rounded-xl p-4 shadow-sm bg-white">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-blue-600">
          Auction Insights Upload
        </h2>
        <button
          className="p-1 text-md bg-gray-400 hover:bg-gray-500 text-white rounded-sm"
          onClick={() => removeArrayIndex(arrayIndex)}
        >
          <IoMdClose />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-4 border-b pb-4 mb-4">
        <div>
          <label className="mr-1.5 text-sm text-gray-600">Upload File:</label>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            className="text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {originalData.length > 0 && !error && (
          <>
            <div>
              <label className="mr-1.5 text-sm text-gray-600">
                Label (X-Axis):
              </label>
              <select
                value={labelColumn}
                onChange={(e) => setLabelColumn(e.target.value)}
                className="border border-gray-300 rounded-md p-1.5 text-sm"
              >
                {stringColumns.map((header) => (
                  <option key={header} value={header}>
                    {header}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mr-1.5 text-sm text-gray-600">
                Metric (Y-Axis):
              </label>
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="border border-gray-300 rounded-md p-1.5 text-sm"
              >
                {numericColumns.map((metric) => (
                  <option key={metric} value={metric}>
                    {metric}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mr-1.5 text-sm text-gray-600">
                Chart Type:
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="border border-gray-300 rounded-md p-1.5 text-sm"
              >
                <option value="pie">Pie Chart</option>
                <option value="line">Line Chart</option>
                <option value="bar">Bar Chart</option>
              </select>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-center items-center w-full h-[350px]">
        {loading ? (
          <div className="text-center text-gray-600 text-sm">
            Processing file...
          </div>
        ) : error ? (
          <div className="text-center text-red-500 text-sm">{error}</div>
        ) : originalData.length > 0 ? (
          renderChart()
        ) : (
          <div className="text-center text-gray-500 text-sm">
            Please upload an Excel file to generate a chart.
          </div>
        )}
      </div>
    </div>
  );
};

export default AuctionInsightsUploadChart;
